import 'swiper/components/navigation/navigation.min.css';
import 'swiper/swiper.min.css';

import { PuscicaDesno } from 'kzv-icons';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';

import SinglePoiCrumbs from 'components/breadcrumbs/SinglePoiCrumbs';
import Button from 'components/buttons/Button';
import CommentList from 'components/comments/CommentList';
import DividerCurve from 'components/curves/DividerCurve';
import ThinDividerCurve from 'components/curves/svg/ThinDividerCurve';
import Footer from 'components/Footer';
import MapModal from 'components/modals/MapModal';
import PoiPhotoUploadModal from 'components/modals/PoiPhotoUploadModal';
import AddPhotosButton from 'components/poi/gallery/AddPhotosButton';
import NearbyPoisGrid from 'components/poi/NearbyPoisGrid';
import PhotoUploadCTA from 'components/poi/PhotoUploadCTA';
import PoiDescription from 'components/poi/PoiDescription';
import PoiHeadlineBar from 'components/poi/PoiHeadlineBar';
import PoiMapAndInfoBoxes from 'components/poi/PoiMapAndInfoBoxes';
import PoiMediaGallery from 'components/poi/PoiMediaGallery';
import PoiMountainPaths from 'components/poi/PoiMountainPaths';
import SuggestedPois from 'components/poi/SuggestedPois';
import pages from 'config/pages';
import { setLoginDialogActive } from 'contexts/AppActions';
import AppContext from 'contexts/AppContext';
import { fetchCommentsByPoiId, fetchPoiBySlug } from 'data/fetchers';
import useUser from 'hooks/useUser';
import Description from 'models/Description';
import { dehydrate, QueryClient, useInfiniteQuery, useQuery } from 'react-query';
import { Mixpanel } from 'utils/Mixpanel';

export async function getStaticPaths() {
    return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params }) {
    if (!params?.slug) return { notFound: true };

    const queryClient = new QueryClient();

    try {
        const response = await queryClient.fetchQuery(['poi', params.slug], () => fetchPoiBySlug(params.slug));
        const poiId = response?.data?.id;
        await queryClient.fetchInfiniteQuery(
            ['poi_comments', poiId],
            ({ pageParam = 1 }) => fetchCommentsByPoiId(poiId, pageParam),
            {
                getNextPageParam: (lastPage) => lastPage.meta.current_page + 1 ?? null
            }
        );
    } catch (error) {
        return {
            notFound: true
        };
    }

    // Pass post data to the page via props
    return {
        props: {
            dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
            params
        },
        revalidate: 60 * 60 * 6 // In seconds
    };
}

const SinglePoi = ({ params }) => {
    const { dispatch } = useContext(AppContext);
    const { user } = useUser();
    const router = useRouter();
    const [mapModalOpened, setMapModalOpened] = useState(false);
    const [photoUploadModalOpened, setPhotoUploadModalOpened] = useState(false);
    const {
        data: { data: poi }
    } = useQuery(['poi', params.slug], fetchPoiBySlug);
    const {
        data: comments,
        fetchNextPage,
        hasNextPage
    } = useInfiniteQuery(['poi_comments', poi.id], ({ pageParam }) => fetchCommentsByPoiId(poi.id, pageParam), {
        staleTime: Infinity,
        getNextPageParam: (lastPage) =>
            lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined
    });

    let poiDescription =
        poi.descriptions.find((d) => d.type === 'manual') || poi.descriptions.find((d) => d.type === 'auto');

    if (!poiDescription) poiDescription = { content: pages.ideas.seo.description } as Description;

    const featuredPhoto = poi?.photos?.find((photo) => photo.media_type === 'featured-photo');
    let shareImage = featuredPhoto?.files?.conversions?.facebook?.url || featuredPhoto?.files?.original?.url;
    if (!featuredPhoto) {
        const firstPhoto = poi.photos?.[0];
        shareImage = firstPhoto?.files?.conversions?.facebook?.url || firstPhoto?.files?.original?.url;
    }
    const pageMeta = pages.idea(poi, poiDescription);

    const openPhotoModal = () => {
        if (user) {
            setPhotoUploadModalOpened(true);
        } else {
            setLoginDialogActive(dispatch, true);
        }
    };

    useEffect(() => {
        if (mapModalOpened) {
            Mixpanel.track('Poi map opened');
        }
    }, [mapModalOpened]);

    if (router.isFallback) {
        return <h1 className="py-6 text-center">Nalagam...</h1>;
    }

    const ogShare = {
        description: pageMeta.seo.description,
        title: `${pageMeta.title} | Kam za vikend`,
        url: pageMeta.seo.url,
        images: []
    };
    if (shareImage) {
        ogShare.images = [{ url: shareImage }];
    }

    return (
        <>
            <NextSeo title={pageMeta.title} description={pageMeta.seo.description} openGraph={ogShare} />
            <div className="single-page idea">
                <section id="breadcrumbs" className="container mt-6">
                    <SinglePoiCrumbs
                        region={poi.areas.find((area) => area.type.key === 'region')}
                        municipality={poi.areas.find((area) => area.type.key === 'municipality')}
                        category={poi.type.default_category}
                    />
                </section>

                <PoiHeadlineBar poi={poi} />

                <section id="main-content" className="container">
                    <div className="grid justify-between grid-cols-12 sm:gap-8">
                        <div className="col-span-12 md:col-span-7 lg:col-span-8">
                            {poi.photos.length > 0 ? (
                                <>
                                    <PoiMediaGallery media={poi.photos} poiName={poi.name} poiId={poi.id} />
                                    <div className="mt-4 md:mt-2">
                                        <AddPhotosButton openPhotoUploadModal={openPhotoModal} poi={poi} />
                                    </div>
                                </>
                            ) : (
                                <PhotoUploadCTA handleClick={openPhotoModal} poi={poi} />
                            )}

                            <div className="mt-12 md:hidden">
                                <PoiMapAndInfoBoxes poi={poi} handleMapOpen={() => setMapModalOpened(true)} />
                            </div>
                            <div className="mt-8" id="opis">
                                <PoiDescription descriptions={poi.descriptions} />
                            </div>
                            {poi.mountain_paths?.length > 0 && (
                                <div className="mt-8">
                                    <h2 className="mb-4 font-serif uppercase text-brand">Poti</h2>
                                    <PoiMountainPaths paths={poi.mountain_paths} />
                                </div>
                            )}

                            <div className="mt-12" id="comments">
                                {comments.pages.flatMap((page) => page.data).length > 0 && (
                                    <>
                                        <ThinDividerCurve styles="h-6" />
                                        <h4 className="mt-4 mb-2 font-serif">Komentarji in izkušnje uporabnikov</h4>
                                        <CommentList
                                            comments={comments.pages.flatMap((page) => page.data)}
                                            handleLoadMore={() => fetchNextPage()}
                                            hasNextPage={hasNextPage}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <div className={`hidden md:block col-span-5 lg:col-span-4`}>
                            <PoiMapAndInfoBoxes poi={poi} handleMapOpen={() => setMapModalOpened(true)} />
                        </div>
                    </div>
                </section>

                {poi.location && (
                    <section id="v-blizini" className="mt-16">
                        <div className="container">
                            <h2 className="font-serif text-center uppercase text-brand">V bližini</h2>
                            <div className="mt-8">
                                <NearbyPoisGrid lat={poi.location.lat} lng={poi.location.lng} exclude={[poi.id]} />
                            </div>
                        </div>
                    </section>
                )}

                <section id="sorodne-ideje" className="relative pt-12 pb-24 bg-gray-200 mt-14">
                    <DividerCurve styles="absolute w-full left-0 -top-2" />
                    <div className="container">
                        <h2 className="mb-12 font-serif text-center uppercase text-brand">Sorodne ideje za izlet</h2>
                        <SuggestedPois poi={poi} />
                        <div className="mt-8 text-center">
                            <Button link="/ideje" color="secondary" endIcon={<PuscicaDesno strokeWidth={2} />}>
                                Vse ideje za izlet
                            </Button>
                        </div>
                    </div>
                    <DividerCurve styles="absolute w-full left-0 -bottom-2 transform rotate-180" />
                </section>

                {poi.location && mapModalOpened && (
                    <MapModal isOpen={mapModalOpened} handleClose={() => setMapModalOpened(false)} selectedPoi={poi} />
                )}

                <PoiPhotoUploadModal
                    open={photoUploadModalOpened}
                    handleClose={() => setPhotoUploadModalOpened(false)}
                    poi={poi}
                />

                <Footer />
            </div>
        </>
    );
};

export default SinglePoi;
